import * as yup from 'yup';
import { homeworkDefaultTitleKey, type HomeworkTitleTarget } from '../homeworkTitles';

/**
 * E34 · Homework — the both-ends input codecs of the homework mutations (plan 03 §2.1 #3):
 * the portal and the phone validate with the SAME schema before sending, the API runs it again as
 * the shield's inputRule. Shape only — the business rules (a past due instant, one deadline shape,
 * a target per kind, who may act) are the deciders', where they are binding.
 *
 * The three vocabularies below are the board's and MUST equal `@cc/domain`'s (`HOMEWORK_KINDS`,
 * `HOMEWORK_REMINDER_POLICIES`, `HOMEWORK_SKIP_REASON_CODES`) — `@cc/domain` depends on this package,
 * not the reverse, so the lists live twice and a domain spec (`homework/specs/wire.spec.ts`) pins
 * them equal.
 *
 * `requester`, `practiceId`, `assignmentId` (on Assign), `performer` and `evidence` are ABSENT on
 * purpose: they are the server's (the caller, the caller's practice, a minted id) or the evidence
 * matcher's. A client that could name them could write homework into somebody else's record or
 * complete a tool without doing it.
 */
export const HOMEWORK_KIND_VALUES = [
  'tool',
  'assessment',
  'worksheet',
  'reading',
  'task',
  'link',
  'session',
  'video',
] as const;
export const HOMEWORK_REMINDER_POLICY_VALUES = [
  'default',
  'dueOnly',
  'none',
] as const;
export const HOMEWORK_SKIP_REASON_VALUES = [
  'notTime',
  'notRelevant',
  'tooHard',
  'other',
] as const;

const assignmentId = yup.string().trim().min(1).max(128).required();
const userRef = yup
  .string()
  .trim()
  .matches(/^users\/[A-Za-z0-9_-]{1,64}$/);
const instant = yup.date().nullable();
const shortText = (max: number) => yup.string().trim().max(max).nullable();

export const homeworkTargetSchema = yup
  .object({
    type: yup.string().trim().min(1).max(64).required(),
    ref: yup.string().trim().max(2048).nullable(),
    version: yup.number().integer().min(1).nullable(),
    source: yup.string().oneOf(['learn', 'youtube', 'vimeo', null]).nullable(),
  })
  .default(undefined)
  .nullable();

/**
 * The E05 ReminderSchedule value. A homework schedule must carry a real zone (`tzid`): an occurrence
 * has to be an INSTANT to be due or overdue. Whether the rule itself is supported is the E05
 * validator's question, asked by the API (`validateSchedule`), not repeated here.
 */
export const homeworkScheduleSchema = yup
  .object({
    rrule: yup.string().trim().min(1).max(512).required(),
    dtstart: yup.string().trim().min(10).max(40).required(),
    tzid: yup.string().trim().min(1).max(64).required(),
    exdates: yup.array().of(yup.string().trim().max(40).required()).nullable(),
  })
  .default(undefined)
  .nullable();

/**
 * E43 · a task's ACTIVITY TARGET (cc-homework, AssignHomework / HomeworkAssigned `activityTarget`, the
 * founder's Q-e43g1-8 A, 2026-10-09): `{kind:"workout", workoutType:"walking", minutesEach:20, times:3,
 * per:"week"}`. The workout types are the ones the phone's health adapter reports and the evidence
 * matcher compares (`workout.type === workoutType`); an absent / null `workoutType` is "any workout".
 * Whole minutes and whole times, positive; `times` is days a week (the matcher counts one per day), so
 * 1–7; one `per` — "week". Unknown keys are refused: the matcher reads exactly these.
 */
export const HOMEWORK_WORKOUT_TYPE_VALUES = [
  'walking',
  'running',
  'cycling',
  'swimming',
  'yoga',
  'strength',
  'hiking',
  'dance',
  'other',
] as const;
export const HOMEWORK_ACTIVITY_PER_VALUES = ['week'] as const;
export const HOMEWORK_ACTIVITY_MINUTES_MAX = 600;
export const HOMEWORK_ACTIVITY_TIMES_MAX = 7;

export const homeworkActivityTargetSchema = yup
  .object({
    kind: yup.string().oneOf(['workout']).required(),
    workoutType: yup
      .string()
      .oneOf([...HOMEWORK_WORKOUT_TYPE_VALUES, null])
      .nullable(),
    minutesEach: yup
      .number()
      .integer()
      .min(1)
      .max(HOMEWORK_ACTIVITY_MINUTES_MAX)
      .required(),
    times: yup
      .number()
      .integer()
      .min(1)
      .max(HOMEWORK_ACTIVITY_TIMES_MAX)
      .required(),
    per: yup
      .string()
      .oneOf([...HOMEWORK_ACTIVITY_PER_VALUES])
      .required(),
  })
  // a closed shape (yup's `noUnknown` only strips on cast, and a stripped key would pass silently)
  .test(
    'known-keys',
    'activityTarget has a field the matcher does not read',
    (value, ctx) =>
      value == null ||
      Object.keys(value as object).every((k) =>
        Object.keys(ctx.schema.fields).includes(k),
      ),
  )
  .default(undefined)
  .nullable();

export const assignHomeworkInputSchema = yup.object({
  subject: userRef.nullable(),
  kind: yup
    .string()
    .oneOf([...HOMEWORK_KIND_VALUES])
    .required(),
  target: homeworkTargetSchema,
  // Q-ru-3 A (2026-10-24): null = the tool's DEFAULT title, left as it is — each reader renders it in its own language
  // (`homeworkTitles.ts`); only a tool target with a default may go without one (the decider's HOMEWORK_TITLE_REQUIRED)
  title: yup
    .string()
    .trim()
    .min(1)
    .max(200)
    .nullable()
    .test(
      'title-or-default',
      'title is required unless the target has a default title',
      function (value) {
        const { kind, target } = this.parent as { kind?: string; target?: HomeworkTitleTarget | null };
        return value != null || homeworkDefaultTitleKey(kind, target) !== null;
      },
    ),
  // the name a default title quotes (functional analysis: the behavior) — sent only with a default title
  titleName: yup.string().trim().max(200).nullable(),
  instructions: shortText(4000),
  activityTarget: homeworkActivityTargetSchema,
  dueAt: instant,
  schedule: homeworkScheduleSchema,
  reminderPolicy: yup
    .string()
    .oneOf([...HOMEWORK_REMINDER_POLICY_VALUES, null])
    .nullable(),
  // Q-paper-3 B (1.5.0): a tool's "may do it on paper" switch — the decider keeps it for a tool only
  paperAllowed: yup.boolean().nullable(),
});

export const amendHomeworkInputSchema = yup.object({
  assignmentId,
  title: yup.string().trim().min(1).max(200),
  instructions: shortText(4000),
  dueAt: instant,
  // block 1e (the founder's Q-b1a-5 B): a recurring row's new repeat rule — the same shape as Assign's
  schedule: homeworkScheduleSchema,
  reminderPolicy: yup.string().oneOf([...HOMEWORK_REMINDER_POLICY_VALUES]),
  // Q-paper-3 B (1.5.0): a tool's paper switch, sent only when touched; null = unchanged
  paperAllowed: yup.boolean().nullable(),
});

export const cancelHomeworkInputSchema = yup.object({
  assignmentId,
  reason: yup.string().trim().min(1).max(1000).required(),
});

export const reopenHomeworkInputSchema = yup.object({
  assignmentId,
  reason: shortText(1000),
  dueAt: instant,
});

/**
 * THE PAPER PHOTO'S FILE REFERENCE — `homework-photos/<uuid>.jpg`, relative to the client's own
 * Storage folder `tools/<firebaseUid>/` (the phone's `paperPhotoFileRef`, block 1c). The viewer (block
 * 1e, the founder's Q-b1b-2 A) mints a URL only for a reference of exactly this shape, so no other path
 * of the client's folder can be named through it.
 */
export const HOMEWORK_PAPER_PHOTO_FILE_REF =
  /^homework-photos\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.jpg$/;

export const homeworkPaperPhotoSchema = yup
  .object({
    // the one shape the phone mints and the viewer serves — both ends (the founder's Q-b1e-2 A, 2026-09-28)
    fileRef: yup.string().trim().matches(HOMEWORK_PAPER_PHOTO_FILE_REF).required(),
    takenAt: yup.date().required(),
  })
  .default(undefined)
  .nullable();

export const completeHomeworkInputSchema = yup.object({
  assignmentId,
  occurrenceAt: instant,
  note: shortText(4000),
  paperPhoto: homeworkPaperPhotoSchema,
});

export const skipHomeworkInputSchema = yup.object({
  assignmentId,
  occurrenceAt: instant,
  reasonCode: yup
    .string()
    .oneOf([...HOMEWORK_SKIP_REASON_VALUES])
    .required(),
  reason: shortText(1000),
});

/** RequestHomeworkReminder (block 1b) — the assignment and the optional line that goes with it. */
export const requestHomeworkReminderInputSchema = yup.object({
  assignmentId,
  message: shortText(500),
});

/**
 * CommentOnHomework (block 4) — the assignment and the comment. 1–2 000 characters AFTER trimming (the
 * decider's `COMMENT_MAX_LENGTH`, pinned equal in `wire.spec.ts`); blank text is the decider's
 * HOMEWORK_COMMENT_TEXT, so the codec only bounds the wire (an unbounded body never reaches the log).
 */
export const HOMEWORK_COMMENT_MAX_LENGTH = 2000;
export const commentOnHomeworkInputSchema = yup.object({
  assignmentId,
  text: yup.string().max(HOMEWORK_COMMENT_MAX_LENGTH * 2).required(),
});

/** homeworkPaperPhotoUrl (block 1e) — the assignment and the photo's reference, as the completion carries it. */
export const homeworkPaperPhotoUrlArgsSchema = yup.object({
  assignmentId,
  fileRef: yup
    .string()
    .trim()
    .matches(HOMEWORK_PAPER_PHOTO_FILE_REF)
    .required(),
});

/**
 * WatchAssignedVideo (block 6) — one viewing of an assigned video from the homework player, as the device
 * queue carries it: the device-minted `viewingId` (UUIDv7 — the event's key, so a resend is a no-op), the
 * assignment, the device's copy of its target, and three whole numbers of seconds. `subject` is ABSENT on
 * purpose (the session's). The device validates with this schema BEFORE it queues (both ends, 00 §8-Q9) so a
 * proposal the server would refuse never jams the queue: never negative, and a position at most
 * `HOMEWORK_VIDEO_OVERRUN_SECONDS` past the duration — E15's `PROGRESS_OVERRUN_TOLERANCE_SECONDS`, the same
 * number the decider holds (`@cc/domain` depends on this package, not the reverse; `wire.spec.ts` pins them).
 */
export const HOMEWORK_VIDEO_OVERRUN_SECONDS = 2;
const seconds = yup.number().integer().min(0).max(24 * 60 * 60 * 10);
export const watchAssignedVideoInputSchema = yup
  .object({
    viewingId: yup
      .string()
      .trim()
      .matches(/^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i)
      .required(),
    assignmentId,
    target: homeworkTargetSchema,
    position: seconds.required(),
    duration: seconds.required(),
    secondsPlayed: seconds.required(),
    occurredAt: yup.date().required(),
  })
  .test(
    'position-within-duration',
    'position is more than the tolerance past the duration',
    (v) =>
      !v ||
      typeof v.position !== 'number' ||
      typeof v.duration !== 'number' ||
      v.position <= v.duration + HOMEWORK_VIDEO_OVERRUN_SECONDS,
  );

/** The mutation ARGUMENTS — what the shield's inputRules validate. */
export const assignHomeworkArgsSchema = yup.object({
  input: assignHomeworkInputSchema.required(),
});
export const amendHomeworkArgsSchema = yup.object({
  input: amendHomeworkInputSchema.required(),
});
export const cancelHomeworkArgsSchema = yup.object({
  input: cancelHomeworkInputSchema.required(),
});
export const reopenHomeworkArgsSchema = yup.object({
  input: reopenHomeworkInputSchema.required(),
});
export const acknowledgeHomeworkArgsSchema = yup.object({ assignmentId });
export const completeHomeworkArgsSchema = yup.object({
  input: completeHomeworkInputSchema.required(),
});
export const skipHomeworkArgsSchema = yup.object({
  input: skipHomeworkInputSchema.required(),
});
export const requestHomeworkReminderArgsSchema = yup.object({
  input: requestHomeworkReminderInputSchema.required(),
});
export const commentOnHomeworkArgsSchema = yup.object({
  input: commentOnHomeworkInputSchema.required(),
});
/** homeworkComments (block 4) — the assignment whose thread is read. */
export const homeworkCommentsArgsSchema = yup.object({ assignmentId });
export const watchAssignedVideoArgsSchema = yup.object({
  input: watchAssignedVideoInputSchema.required(),
});

export const homeworkValidators = {
  assignHomeworkInputSchema,
  amendHomeworkInputSchema,
  cancelHomeworkInputSchema,
  reopenHomeworkInputSchema,
  completeHomeworkInputSchema,
  skipHomeworkInputSchema,
  requestHomeworkReminderInputSchema,
  commentOnHomeworkInputSchema,
  assignHomeworkArgsSchema,
  amendHomeworkArgsSchema,
  cancelHomeworkArgsSchema,
  reopenHomeworkArgsSchema,
  acknowledgeHomeworkArgsSchema,
  completeHomeworkArgsSchema,
  skipHomeworkArgsSchema,
  requestHomeworkReminderArgsSchema,
  commentOnHomeworkArgsSchema,
  homeworkPaperPhotoUrlArgsSchema,
  watchAssignedVideoInputSchema,
  watchAssignedVideoArgsSchema,
};
